// Photoshoots table (PX-386) — filterable + multi-level groupable.
//
// TanStack Table v8 is the engine (grouping / expansion / column visibility /
// sorting / cell rendering). Filtering + faceting + array-dim explosion are done
// in plain code (see model.ts) because array-valued tags/plan don't map cleanly
// onto TanStack's single-value column filters. Everything is client-side — the
// admin list endpoint returns the full catalog.
import { useEffect, useMemo, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  type VisibilityState,
  type ExpandedState,
  type GroupingState,
  type SortingState,
} from '@tanstack/react-table'
import type { AdminPhotoshootResponse, AdminPhotoshootUpdate, AdminPlanResponse } from '@/types/api'
import { buildColumns, COLUMN_LABELS } from './columns'
import { ToolPanel, type PanelTab } from './ToolPanel'
import {
  applyFilters,
  computeFacets,
  EMPTY_FILTERS,
  effectiveModels,
  explode,
  formatGroupValue,
  hasActiveFilters,
  type TableFilters,
} from './model'

const STORAGE_KEY = 'pinxie-admin:photoshoots-table:v1'

interface Persisted {
  grouping?: GroupingState
  columnVisibility?: VisibilityState
  filters?: TableFilters
  panelTab?: PanelTab
  sorting?: SortingState
}

function loadPersisted(): Persisted {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Persisted
  } catch {
    return {}
  }
}

export function PhotoshootsTable({
  photoshoots,
  plans,
  isLoading,
  patching,
  onOpen,
  onPatch,
}: {
  photoshoots: AdminPhotoshootResponse[]
  plans: AdminPlanResponse[]
  isLoading: boolean
  patching: boolean
  onOpen: (ps: AdminPhotoshootResponse) => void
  onPatch: (id: string, body: AdminPhotoshootUpdate) => void
}) {
  const persisted = useMemo(loadPersisted, [])
  const [grouping, setGrouping] = useState<GroupingState>(persisted.grouping ?? [])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    persisted.columnVisibility ?? {},
  )
  const [sorting, setSorting] = useState<SortingState>(persisted.sorting ?? [])
  // Merge over EMPTY_FILTERS so a persisted blob from before a new field was
  // added (e.g. PX-448 `kind`) still has every key defined.
  const [filters, setFilters] = useState<TableFilters>({ ...EMPTY_FILTERS, ...persisted.filters })
  const [panelTab, setPanelTab] = useState<PanelTab>(persisted.panelTab ?? null)
  // Groups open by default so the tree is visible immediately after grouping.
  const [expanded, setExpanded] = useState<ExpandedState>(true)

  useEffect(() => {
    const data: Persisted = { grouping, columnVisibility, filters, panelTab, sorting }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }, [grouping, columnVisibility, filters, panelTab, sorting])

  const facets = useMemo(() => computeFacets(photoshoots, plans), [photoshoots, plans])
  const filtered = useMemo(() => applyFilters(photoshoots, filters), [photoshoots, filters])
  const data = useMemo(() => explode(filtered, grouping), [filtered, grouping])

  const columns = useMemo(
    () => buildColumns({ plans, patching, onPatch, onOpen }),
    [plans, patching, onPatch, onOpen],
  )

  const labelCtx = useMemo(
    () => ({
      planName: new Map(plans.map((p) => [p.id, p.name])),
      modelName: new Map(
        photoshoots.flatMap((p) => effectiveModels(p).map((m) => [m.slug, m.label] as const)),
      ),
    }),
    [plans, photoshoots],
  )

  const table = useReactTable({
    data,
    columns,
    state: { grouping, columnVisibility, sorting, expanded },
    getRowId: (r) => r._rowKey,
    onGroupingChange: setGrouping,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    groupedColumnMode: false,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const visibleColumns = table.getVisibleLeafColumns()
  const rows = table.getRowModel().rows
  const grouped = grouping.length > 0

  const columnToggles = table
    .getAllLeafColumns()
    .map((c) => ({ id: c.id, visible: c.getIsVisible() }))

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar: search + count + expand/collapse + clear */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search name or slug…"
          className="w-60 rounded-md border border-ink-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
        <span className="text-xs text-ink-400">{filtered.length} of {photoshoots.length}</span>
        {hasActiveFilters(filters) && (
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="text-xs text-ink-400 underline-offset-2 transition hover:text-ink-700 hover:underline"
          >
            Clear filters
          </button>
        )}
        {grouped && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="rounded-md border border-ink-200 px-2 py-1 text-xs font-medium text-ink-600 hover:border-ink-300"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => setExpanded({})}
              className="rounded-md border border-ink-200 px-2 py-1 text-xs font-medium text-ink-600 hover:border-ink-300"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      <div className="flex items-stretch rounded-xl border border-ink-100 bg-white shadow-sm">
        <div className="min-w-0 flex-1 overflow-x-auto">
          {/* min-w sets the floor below which the parent overflow-x-auto
              scrolls horizontally instead of squeezing columns (and slivering
              the cover thumbnails). w-full still fills when there's room. */}
          <table className="w-full min-w-[1024px] text-sm">
            <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
              <tr>
                {visibleColumns.map((col) => {
                  const sortable = col.getCanSort()
                  const sorted = col.getIsSorted()
                  return (
                    <th
                      key={col.id}
                      onClick={sortable ? col.getToggleSortingHandler() : undefined}
                      className={`whitespace-nowrap px-3 py-2 font-semibold ${sortable ? 'cursor-pointer select-none hover:text-ink-600' : ''}`}
                    >
                      {COLUMN_LABELS[col.id] ?? col.id}
                      {sorted === 'asc' ? ' ▲' : sorted === 'desc' ? ' ▼' : ''}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={visibleColumns.length} className="px-3 py-6 text-center text-sm text-ink-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length} className="px-3 py-6 text-center text-sm text-ink-400">
                    No photoshoots match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                if (row.getIsGrouped()) {
                  const colId = row.groupingColumnId ?? ''
                  const label = formatGroupValue(colId, row.getGroupingValue(colId), labelCtx)
                  return (
                    <tr key={row.id} className="border-b border-ink-100 bg-ink-50/60">
                      <td colSpan={visibleColumns.length} className="px-3 py-1.5">
                        <button
                          type="button"
                          onClick={row.getToggleExpandedHandler()}
                          style={{ paddingLeft: `${row.depth * 18}px` }}
                          className="flex items-center gap-2 text-left"
                        >
                          <span className="text-ink-400">{row.getIsExpanded() ? '▾' : '▸'}</span>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-300">
                            {COLUMN_LABELS[colId] ?? colId}
                          </span>
                          <span className="font-medium text-ink-800">{label}</span>
                          <span className="rounded-full bg-ink-200/70 px-1.5 py-0.5 text-[11px] font-medium text-ink-600">
                            {row.getLeafRows().length}
                          </span>
                        </button>
                      </td>
                    </tr>
                  )
                }
                return (
                  <tr
                    key={row.id}
                    onClick={() => onOpen(row.original)}
                    className="cursor-pointer border-b border-ink-100 transition last:border-b-0 hover:bg-brand-50/40"
                  >
                    {row.getVisibleCells().map((cell, i) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 align-middle"
                        style={i === 0 && grouped ? { paddingLeft: `${(row.depth + 1) * 18 + 12}px` } : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <ToolPanel
          tab={panelTab}
          onTabChange={setPanelTab}
          grouping={grouping}
          onGroupingChange={setGrouping}
          filters={filters}
          onFiltersChange={setFilters}
          facets={facets}
          columns={columnToggles}
          onToggleColumn={(id) =>
            setColumnVisibility((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }))
          }
        />
      </div>
    </div>
  )
}
